// Ported from genieos/web/src/hooks/useFocusTrap.ts @ 84923d036
/**
 * Focus Trap Hook for Modal Accessibility
 *
 * Traps focus within a container element for modals and dialogs.
 * Ensures keyboard navigation stays within the modal.
 */

import { useEffect, useRef } from 'react';

/**
 * Focusable descendants, in DOM order.
 *
 * ⚠️ Queried on **every** Tab press rather than once when the trap activates.
 * A container whose contents are swapped while it stays open — a tabbed
 * dialog is the obvious case — otherwise keeps cycling to the nodes that were
 * present at activation. Those nodes are detached after the swap, and
 * `focus()` on a detached node is a silent no-op, so Tab walks straight out
 * of the dialog and into the page behind it. Re-querying is a single
 * `querySelectorAll` per keypress and removes the staleness entirely, without
 * asking each caller to declare when its contents change.
 *
 * `:not([disabled])` matters for the same reason: a disabled trailing button
 * cannot take focus, so cycling to it also lets Tab escape.
 */
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    // Store previously focused element
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus first element on mount. Captured once, deliberately: this is the
    // entry focus, and re-running it while the user is interacting would yank
    // focus back to the top of the dialog.
    const firstOnEntry = getFocusable(container)[0];
    let entryFocusFrame: number | null = null;
    if (firstOnEntry) {
      // Small delay to ensure element is ready
      entryFocusFrame = requestAnimationFrame(() => {
        entryFocusFrame = null;
        firstOnEntry.focus();
      });
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable(container);
      if (focusable.length === 0) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);

      // Cancel the entry focus if the trap deactivates before the frame lands.
      //
      // ⚠️ This must happen, and it must happen BEFORE the restore below.
      // A trap that closes inside one frame otherwise still runs
      // `firstOnEntry.focus()` afterwards and silently undoes its own focus
      // restoration — focus lands back inside a dialog the operator has just
      // dismissed, with no keydown handler left to trap it.
      //
      // It is NOT saved by the node being detached: `Modal` (gamma) keeps the
      // container in the DOM for up to 400 ms after `isOpen` goes false so
      // AnimatePresence can play its exit, and `useFocusTrap(isOpen)` runs
      // this cleanup at the START of that window. `firstOnEntry` is still
      // attached, so `focus()` is a real focus steal rather than the no-op an
      // unmounted node would give.
      if (entryFocusFrame !== null) {
        cancelAnimationFrame(entryFocusFrame);
        entryFocusFrame = null;
      }

      // Restore focus when component unmounts
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [isActive]);

  return containerRef;
}

export default useFocusTrap;
